// Autosaves the demo form for this visit (one record per tab session, keyed by a random draft key)
// and restores it after a refresh or going back. Identical snapshots are never re-sent.
import { useEffect, useRef, useState } from "react";
import { formConfig } from "@/lib/formAnalytics";

const KEY = "cy_demo_draft_key";
const VALUES = "cy_demo_draft_values";
const ss = () => window.sessionStorage;
const newKey = () => (window.crypto?.randomUUID?.() || `${Date.now()}${Math.random().toString(36).slice(2)}`).replace(/[^A-Za-z0-9_-]/g, "");

function draftKey() {
  let k = ss().getItem(KEY);
  if (!k) { k = newKey(); ss().setItem(KEY, k); }
  return k;
}

const clean = (f) => Object.fromEntries(Object.entries(f).filter(([k, v]) =>
  k !== "website_hp" && (Array.isArray(v) ? v.length > 0 : String(v ?? "").trim() !== "")));

function send(s, keepalive = false) {
  const fields = clean(s.form);
  if (s.done || !Object.keys(fields).length) return;
  const body = JSON.stringify({ form_id: "demo_request", page_path: window.location.pathname, fields });
  if (body === s.last) return;
  s.last = body;
  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/form-drafts/${draftKey()}`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive, credentials: "same-origin",
  }).catch(() => { s.last = ""; });  // autosave never breaks the form; retry on the next change
}

export function useDemoAutosave(form, setForm, enabled) {
  const [on, setOn] = useState(false);
  const st = useRef({ last: "", done: false, form });
  st.current.form = form;

  useEffect(() => {
    if (!enabled) return;
    formConfig().then((c) => {
      if (!c?.form_autosave) return;
      try {
        const v = JSON.parse(ss().getItem(VALUES) || "null");
        if (v) setForm((f) => ({ ...f, ...v }));
      } catch { /* ignore unreadable saved values */ }
      setOn(true);
    });
  }, [enabled, setForm]);

  useEffect(() => {
    if (!on) return undefined;
    ss().setItem(VALUES, JSON.stringify(clean(form)));
    const t = setTimeout(() => send(st.current), 800);
    return () => clearTimeout(t);
  }, [on, form]);

  useEffect(() => {
    if (!on) return undefined;
    const s = st.current;
    const flush = () => send(s, true);
    const onHide = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onHide);
    return () => { window.removeEventListener("pagehide", flush); document.removeEventListener("visibilitychange", onHide); flush(); };
  }, [on]);

  return {
    key: () => (on ? draftKey() : undefined),
    finish: () => { st.current.done = true; ss().removeItem(KEY); ss().removeItem(VALUES); },
  };
}
